import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, Save, AlertTriangle } from 'lucide-react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  checklistId: string;
  initial: {
    periodo: string | null;
    tipo_revision: string;
    numero_trimestral: number | null;
    fecha: string;
  };
  onSaved: () => void;
}

const monthToPeriodo = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export const EditarMetadataChecklistDialog: React.FC<Props> = ({ open, onOpenChange, checklistId, initial, onSaved }) => {
  const [periodo, setPeriodo] = useState<string>('');
  const [tipo, setTipo] = useState<string>('trimestral');
  const [numeroT, setNumeroT] = useState<string>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPeriodo(initial.periodo || monthToPeriodo(initial.fecha));
    setTipo(initial.tipo_revision || 'trimestral');
    setNumeroT(initial.numero_trimestral ? String(initial.numero_trimestral) : '');
  }, [open, initial]);

  const handleSave = async () => {
    if (!/^\d{4}-\d{2}$/.test(periodo)) {
      toast.error('Periodo inválido. Usa formato AAAA-MM.');
      return;
    }
    if (tipo === 'trimestral' && !numeroT) {
      toast.error('Para trimestral indica 1T, 2T o 3T.');
      return;
    }
    setSaving(true);
    try {
      // 1) Recuperar la fila actual para conocer cliente_id, sistema_id, periodo/tipo antiguos.
      const { data: actual, error: errFetch } = await (supabase as any)
        .from('checklists_seguridad')
        .select('id, cliente_id, sistema_id, tipo_revision, periodo')
        .eq('id', checklistId)
        .maybeSingle();
      if (errFetch || !actual) {
        toast.error('No se pudo leer la revisión.');
        return;
      }

      // 2) Aplicar el update sobre ESTA fila (nunca crea filas nuevas).
      const update: any = {
        periodo,
        tipo_revision: tipo,
        numero_trimestral: tipo === 'trimestral' ? parseInt(numeroT, 10) : null,
      };
      const { error: errUpd } = await (supabase as any)
        .from('checklists_seguridad')
        .update(update)
        .eq('id', checklistId);
      if (errUpd) {
        toast.error('No se pudo actualizar: ' + errUpd.message);
        return;
      }

      // 3) Consolidar duplicados: archivar cualquier OTRA fila no archivada del mismo
      //    cliente+sistema que comparta (tipo_revision, periodo) con el valor ANTIGUO
      //    o con el NUEVO. Estas filas eran duplicados ocultos por el dedup y, al
      //    cambiar la fila editada, aparecerían como si fueran "checklists nuevos".
      if (actual.sistema_id) {
        const combos = [
          { tipo: actual.tipo_revision, periodo: actual.periodo },
          { tipo, periodo },
        ];
        let archivados = 0;
        for (const combo of combos) {
          if (!combo.tipo || !combo.periodo) continue;
          const { data: hermanos } = await (supabase as any)
            .from('checklists_seguridad')
            .select('id')
            .eq('cliente_id', actual.cliente_id)
            .eq('sistema_id', actual.sistema_id)
            .eq('tipo_revision', combo.tipo)
            .eq('periodo', combo.periodo)
            .eq('archivado', false)
            .neq('id', checklistId);
          const ids = (hermanos || []).map((r: any) => r.id);
          if (ids.length === 0) continue;
          await (supabase as any)
            .from('checklists_seguridad')
            .update({ archivado: true })
            .in('id', ids);
          archivados += ids.length;
        }
        if (archivados > 0) {
          toast.success(`Revisión actualizada. Se archivaron ${archivados} duplicado(s) preexistentes.`);
        } else {
          toast.success('Revisión actualizada.');
        }
      } else {
        toast.success('Revisión actualizada.');
      }

      onSaved();
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Editar datos de la revisión</DialogTitle>
          <DialogDescription>
            Cambia el periodo, el tipo o el número de la trimestral sin reabrir el checklist completo.
            La fila se modifica en sitio: no se crea ningún checklist nuevo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="md-periodo">Periodo (AAAA-MM)</Label>
            <Input
              id="md-periodo"
              type="month"
              value={periodo}
              onChange={(e) => setPeriodo(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Tipo de revisión</Label>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="trimestral">Trimestral</SelectItem>
                <SelectItem value="anual">Anual</SelectItem>
                <SelectItem value="semestral">Semestral</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {tipo === 'trimestral' && (
            <div className="space-y-1.5">
              <Label>Número de trimestral (PCI)</Label>
              <div className="flex gap-2">
                {([1, 2, 3] as const).map((n) => (
                  <Button
                    key={n}
                    type="button"
                    variant={numeroT === String(n) ? 'default' : 'outline'}
                    className="flex-1"
                    onClick={() => setNumeroT(String(n))}
                  >
                    {n}T
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Ciclo: Anual → 1T → 2T → 3T → Anual.
              </p>
            </div>
          )}

          <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-2 text-xs text-amber-800 dark:text-amber-300">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              Si existen otros checklists antiguos del mismo sistema con el mismo tipo y periodo,
              se archivarán automáticamente para evitar duplicados.
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
